// 写作（英文日报）：首页（今天这一篇、本周写过的、周复盘、这周练什么）、写作页（只有纸；选中一段才有改写 / 问教练，
// 教练在按钮后面；完成时先看一眼最该改的两三处再归档）、已归档的文章（这篇记下的写法）。
// 服务端是 lingzhi-studio。工作复盘、周复盘存进知识库「英文日报」；私人日记只留在 lingzhi-studio（用户 2026-10-11 定的）。

import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { App as AntApp, Dropdown, Modal } from 'antd'
import { ArrowLeft, BookOpen, Check, CloudOff, Lock, MessageCircle, MoreHorizontal, NotebookPen, RotateCcw, Sparkles } from 'lucide-react'
import {
  archiveDraft,
  countWords,
  createDraft,
  dayLabel,
  discardDraft,
  editSelection,
  generateWeekly,
  getDraft,
  getEntry,
  getWeekly,
  KIND_LABEL,
  isoWeek,
  lastWeek,
  learning,
  localDate,
  publishWeekly,
  reopenEntry,
  restoreDraft,
  reviewDraft,
  timeline,
  type Draft,
  type EditProposal,
  type Entry,
  type EntryKind,
  type Review,
} from '../../api/write'
import { IconButton, Pill, Segmented } from '../../ui'
import { diffWords } from '../../lib/diff'
import { useDocPreview } from '../kb/usePreview'
import { Coach, type Selection } from './Coach'
import { checkEnglish, issueLabel, type LocalIssue } from './grammar'
import { useDraft, type SaveStatus } from './useDraft'
import '../chat/chat.css'
import '../kb/attach.css'
import './write.css'

const FOLDER = '英文日报'

const PROMPTS: Record<EntryKind, string[]> = {
  work: ['What moved forward today?', 'What decision did I make?', 'What is blocking me?', 'What is the next smallest step?'],
  journal: ['What happened today?', 'What did I notice?', 'How did it make me feel?', 'What do I want to remember?'],
}
const WHERE: Record<EntryKind, string> = { work: `归档后存进知识库「${FOLDER}」`, journal: '只留在这里，不进知识库' }
const untitled = (kind: EntryKind) => `未命名${KIND_LABEL[kind]}`
const time = (iso: string) => new Intl.DateTimeFormat('zh-CN', { timeZone: 'Asia/Shanghai', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso))
const shortDay = (iso: string) => new Intl.DateTimeFormat('zh-CN', { timeZone: 'Asia/Shanghai', month: 'numeric', day: 'numeric' }).format(new Date(iso))

// ———— 首页 ————

export function WriteHomePage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { message } = AntApp.useApp()
  const list = useQuery({ queryKey: ['write-timeline'], queryFn: timeline })
  const profile = useQuery({ queryKey: ['write-learning'], queryFn: learning })
  const [weekly, setWeekly] = useState(false)
  const [habits, setHabits] = useState(false)
  const create = useMutation({
    mutationFn: createDraft,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['write-timeline'] })
      navigate(`/write/${d.id}`)
    },
    onError: (e) => void message.error(e.message),
  })
  const drafts = list.data?.drafts ?? []
  const entries = list.data?.entries ?? []
  const today = localDate(new Date())
  const thisWeek = isoWeek(today)
  // 今天这一篇：今天开的草稿就接着写
  const todays = (kind: EntryKind) => drafts.find((d) => d.kind === kind && localDate(d.createdAt) === today)
  const start = (kind: EntryKind) => {
    const d = todays(kind)
    if (d) navigate(`/write/${d.id}`)
    else create.mutate(kind)
  }
  const otherDrafts = drafts.filter((d) => d.id !== todays('work')?.id && d.id !== todays('journal')?.id)
  const week = entries.filter((e) => isoWeek(localDate(e.createdAt)) === thisWeek)
  const earlier = entries.filter((e) => isoWeek(localDate(e.createdAt)) !== thisWeek)
  const entryWeeks = new Set(entries.map((e) => isoWeek(localDate(e.createdAt))))
  const prev = lastWeek()
  const reviewWeek = entryWeeks.has(prev) || !entryWeeks.has(thisWeek) ? prev : thisWeek
  const patterns = profile.data?.patterns ?? []

  return (
    <div className="wh">
      <p className="wh-date">{dayLabel(new Date())}</p>
      <h1>今天想用英文写点什么？</h1>
      <div className="wh-start">
        <Pill variant="primary" size="lg" icon={<NotebookPen />} onClick={() => start('work')} disabled={create.isPending}>
          {todays('work') ? '接着写今天的工作复盘' : '写今天的工作复盘'}
        </Pill>
        <button type="button" className="wh-link" onClick={() => start('journal')} disabled={create.isPending}>
          <Lock width={13} height={13} aria-hidden="true" />
          {todays('journal') ? '接着写今天的日记' : '写日记'}
        </button>
      </div>

      {otherDrafts.length > 0 && (
        <section className="wh-sec" aria-label="没写完的">
          <h2>没写完的</h2>
          {otherDrafts.map((d) => (
            <Link key={d.id} to={`/write/${d.id}`} className="wh-row">
              <span className="t">{d.title || untitled(d.kind)}</span>
              <span className="m">
                {d.kind === 'journal' && '日记 · '}
                {d.words} 词 · {shortDay(d.updatedAt)}
              </span>
            </Link>
          ))}
        </section>
      )}

      <section className="wh-sec" aria-label="本周">
        <h2>本周</h2>
        {week.map((e) => (
          <EntryRow key={e.id} e={e} />
        ))}
        {entryWeeks.has(reviewWeek) && (
          <div className="wh-row">
            <span className="t">周复盘 · {reviewWeek.slice(5)}</span>
            <span className="m">{reviewWeek === thisWeek ? '本周' : '上周'}</span>
            <Pill size="sm" onClick={() => setWeekly(true)}>
              周复盘
            </Pill>
          </div>
        )}
        {!week.length && !entryWeeks.has(reviewWeek) && <p className="wh-none">这周还没写。</p>}
      </section>

      {patterns.length > 0 && (
        <p className="wh-practice">
          这周练：<b>{patterns[0].category}</b>
          <button type="button" className="wh-link" onClick={() => setHabits(true)}>
            写作习惯 {patterns.length}
          </button>
        </p>
      )}

      {earlier.length > 0 && (
        <details className="wh-sec wh-earlier">
          <summary>更早 · {earlier.length} 篇</summary>
          {earlier.map((e) => (
            <EntryRow key={e.id} e={e} />
          ))}
        </details>
      )}

      {list.isError && <p className="sheet-note">写作教练暂时连不上：{list.error.message}</p>}
      {weekly && <WeeklyDialog entryWeeks={entryWeeks} onClose={() => setWeekly(false)} />}
      {habits && (
        <Modal open title="写作习惯" onCancel={() => setHabits(false)} footer={null} width={560} destroyOnHidden>
          <Habits />
        </Modal>
      )}
    </div>
  )
}

function EntryRow({ e }: { e: { id: string; kind: EntryKind; title: string; createdAt: string; knowledgeId?: string } }) {
  return (
    <Link to={`/write/entry/${e.id}`} className="wh-row">
      <span className="t">{e.title}</span>
      <span className="m">
        {e.kind === 'journal' ? '日记 · 只在这里' : e.knowledgeId ? '' : '还没存进知识库 · '}
        {shortDay(e.createdAt)}
      </span>
    </Link>
  )
}

/** 写作习惯：反复出现的写法问题，多的在前（只记写法，不进知识库）。 */
function Habits() {
  const profile = useQuery({ queryKey: ['write-learning'], queryFn: learning })
  const patterns = profile.data?.patterns ?? []
  return (
    <section className="habits" aria-label="写作习惯">
      {patterns.length ? (
        <ul>
          {patterns.slice(0, 12).map((p) => (
            <li key={p.category}>
              <div>
                <b>{p.category}</b>
                <span>出现 {p.occurrences} 次</span>
              </div>
              <p>{p.explanationZh}</p>
              {p.examples.length > 0 && <code>{p.examples.at(-1)}</code>}
            </li>
          ))}
        </ul>
      ) : (
        <p className="sheet-note">每次归档以后，记下英文写法上的问题，出现得多的排在前面；周复盘只拿反复出现的当重点。</p>
      )}
    </section>
  )
}

/** 周复盘：本周或上周（默认上周；上周没写、本周写了就默认本周）。 */
function WeeklyDialog({ entryWeeks, onClose }: { entryWeeks: Set<string>; onClose: () => void }) {
  const now = isoWeek(localDate(new Date()))
  const prev = lastWeek()
  const [week, setWeek] = useState(entryWeeks.has(prev) || !entryWeeks.has(now) ? prev : now)
  return (
    <Modal open title="周复盘" onCancel={onClose} footer={null} width={640} destroyOnHidden>
      <Segmented
        label="哪一周"
        value={week}
        onChange={setWeek}
        items={[
          { value: prev, label: `上周 · ${prev}` },
          { value: now, label: `本周 · ${now}` },
        ]}
      />
      <WeeklyBody key={week} week={week} hasEntries={entryWeeks.has(week)} />
    </Modal>
  )
}

function WeeklyBody({ week, hasEntries }: { week: string; hasEntries: boolean }) {
  const { message } = AntApp.useApp()
  const { open } = useDocPreview()
  const existing = useQuery({ queryKey: ['write-weekly', week], queryFn: () => getWeekly(week) })
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [savedTo, setSavedTo] = useState<{ knowledgeId: string; kbId: string } | null>(null)
  useEffect(() => {
    const w = existing.data
    if (!w) return
    setTitle(w.title)
    setBody(w.body)
    if (w.saved) setSavedTo(w.saved)
  }, [existing.data])
  const gen = useMutation({
    mutationFn: () => generateWeekly(week),
    onSuccess: (w) => {
      setTitle(w.title)
      setBody(w.body)
    },
    onError: (e) => void message.error(e.message),
  })
  const pub = useMutation({
    mutationFn: () => publishWeekly(week, title.trim(), body.trim()),
    onSuccess: (w) => {
      if (w.saved) setSavedTo(w.saved)
      message.success(`已存进「${FOLDER}/周复盘」`)
    },
    onError: (e) => void message.error(e.message),
  })
  const has = !!body
  return (
    <div className="weekly">
      {!has && !gen.isPending && (
        <>
          <p className="sheet-note">
            {hasEntries
              ? '取这一周归档的文章和写作习惯，整理有效的表达、有证据的进步、仍反复出现的问题、下周一个重点和一个 5 分钟练习。私人日记只用来看写法，不会被复述。'
              : '这一周还没有归档的文章。'}
          </p>
          <Pill variant="primary" onClick={() => gen.mutate()} disabled={existing.isPending || !hasEntries}>
            生成复盘
          </Pill>
        </>
      )}
      {gen.isPending && (
        <p className="sheet-note" role="status">
          正在整理上周的写作证据…
        </p>
      )}
      {has && !gen.isPending && (
        <>
          <label className="sr-only" htmlFor="weekly-title">
            复盘标题
          </label>
          <input id="weekly-title" className="weekly-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <label className="sr-only" htmlFor="weekly-body">
            复盘正文
          </label>
          <textarea id="weekly-body" className="weekly-body" value={body} onChange={(e) => setBody(e.target.value)} />
          <p className="sheet-note">存之前可以改。会存进知识库「{FOLDER}/周复盘」，同一周再存会替换上一份。</p>
          <div className="acts">
            <Pill variant="primary" onClick={() => pub.mutate()} disabled={pub.isPending || !title.trim() || !body.trim()}>
              {savedTo ? '更新知识库里的那份' : '存进知识库'}
            </Pill>
            <Pill onClick={() => gen.mutate()} icon={<RotateCcw />}>
              重新生成
            </Pill>
            {savedTo && (
              <Pill variant="ghost" icon={<BookOpen />} onClick={() => open(savedTo.knowledgeId, savedTo.kbId)}>
                打开存好的
              </Pill>
            )}
          </div>
        </>
      )}
    </div>
  )
}

// ———— 写作页 ————

export function WriteDraftPage() {
  const { draftId = '' } = useParams()
  const q = useQuery({ queryKey: ['write-draft', draftId], queryFn: () => getDraft(draftId), staleTime: Infinity, gcTime: 0 })
  if (q.isPending) return <div className="empty-chat" aria-busy="true" />
  if (q.isError) return <p className="empty-chat">没能打开这份草稿：{q.error.message}</p>
  if (q.data.discardedAt) return <Discarded draft={q.data} />
  // 换了一份草稿、或载入了别处改过的最新版，就整个重来（本机的编辑状态跟着这一版走）。
  // 自动保存不改这里的数据，所以平时 revision 不变、不会重来。
  return <Editor key={`${q.data.id}:${q.data.revision}`} draft={q.data} />
}

function Discarded({ draft }: { draft: Draft }) {
  const qc = useQueryClient()
  const restore = useMutation({
    mutationFn: () => restoreDraft(draft.id),
    onSuccess: (d) => {
      qc.setQueryData(['write-draft', d.id], d)
      qc.invalidateQueries({ queryKey: ['write-timeline'] })
    },
  })
  return (
    <div className="empty-chat">
      <p>这份草稿在回收区里。</p>
      <Pill onClick={() => restore.mutate()} icon={<RotateCcw />}>
        撤销，继续写
      </Pill>
    </div>
  )
}

const STATUS: Record<SaveStatus, string> = { saved: '已自动保存', dirty: '正在写…', saving: '正在保存…', error: '没存上，已在本机留了一份', conflict: '这份草稿在别处改过了' }

function Editor({ draft }: { draft: Draft }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { message, modal } = AntApp.useApp()
  const d = useDraft(draft)
  const body = useRef<HTMLTextAreaElement>(null)
  const [selection, setSelection] = useState<Selection | null>(null)
  const [coachOpen, setCoachOpen] = useState(false)
  const [askKey, setAskKey] = useState(0)
  const [proposal, setProposal] = useState<EditProposal | null>(null)
  const [rewriting, setRewriting] = useState(false)
  const [sheet, setSheet] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const requestId = useRef(crypto.randomUUID())
  const issues = useLocalCheck(d.body)

  // 正文高度随内容长
  useEffect(() => {
    const el = body.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [d.body])

  const readSelection = () => {
    const el = body.current
    if (!el) return
    const { selectionStart: from, selectionEnd: to } = el
    const text = d.body.slice(from, to)
    setSelection(to > from && text.trim() ? { from, to, text } : null)
  }

  /** 把正文 [from, to) 换成 text；能用浏览器自己的编辑命令就用（⌘Z 能撤销）。 */
  const replace = (from: number, to: number, text: string) => {
    const el = body.current
    let native = false
    if (el) {
      el.focus()
      el.setSelectionRange(from, to)
      try {
        native = document.execCommand?.('insertText', false, text) ?? false
      } catch {
        native = false
      }
    }
    if (!native || el?.value === d.body) d.update({ body: d.body.slice(0, from) + text + d.body.slice(to) })
    setSelection(null)
    return native
  }

  /** 改写建议对得上原文才改：先看原来的位置，再找全文里唯一的那一处。 */
  const apply = (p: EditProposal) => {
    let at = d.body.slice(p.from, p.to) === p.original ? p.from : -1
    if (at < 0) {
      const first = d.body.indexOf(p.original)
      at = first >= 0 && d.body.indexOf(p.original, first + 1) < 0 ? first : -1
    }
    if (at < 0) return void message.warning('正文已经改过，这条建议对不上了，重新选一次')
    const native = replace(at, at + p.original.length, p.replacement)
    message.success(native ? '改好了，可以用 ⌘Z 撤销' : '改好了')
  }

  const rewrite = async () => {
    if (!selection || rewriting) return
    setRewriting(true)
    try {
      const revision = await d.flush()
      setProposal(await editSelection(draft.id, { revision, from: selection.from, to: selection.to, instruction: '' }))
    } catch (e) {
      message.error(e instanceof Error ? e.message : '改写失败了')
    } finally {
      setRewriting(false)
    }
  }

  const insertPrompt = (prompt: string) => {
    const el = body.current
    const at = el ? el.selectionEnd : d.body.length
    const before = d.body.slice(0, at)
    const text = `${before && !before.endsWith('\n\n') ? (before.endsWith('\n') ? '\n' : '\n\n') : ''}${prompt}\n`
    replace(at, at, text)
  }

  /** 归档前看一眼里点「去改」：选中正文里的那一句。 */
  const goTo = (text: string) => {
    setSheet(false)
    const at = text ? d.body.indexOf(text) : -1
    if (at < 0) return void message.info('正文里没找到这一句，可能已经改过了')
    const el = body.current
    el?.focus()
    el?.setSelectionRange(at, at + text.length)
    readSelection()
  }

  const archive = async () => {
    setArchiving(true)
    try {
      const revision = await d.flush()
      const e = await archiveDraft(draft.id, revision, requestId.current)
      qc.invalidateQueries({ queryKey: ['write-timeline'] })
      qc.invalidateQueries({ queryKey: ['write-learning'] })
      qc.removeQueries({ queryKey: ['write-draft', draft.id] })
      // 重新打开再归档的是同一篇：换成新内容，不然文章页先显示上一次缓存的
      qc.setQueryData(['write-entry', e.id], e)
      message.success(e.kind === 'work' ? `已归档，存进了知识库「${FOLDER}」` : '已归档（私人日记只留在这里）')
      navigate(`/write/entry/${e.id}`, { replace: true })
    } catch (e) {
      message.error(e instanceof Error ? e.message : '归档失败了')
      setArchiving(false)
    }
  }

  const discard = () =>
    modal.confirm({
      title: '把这份草稿移入回收区？',
      content: '已归档的文章不受影响，随时可以撤销。',
      okText: '移入回收区',
      cancelText: '不了',
      onOk: async () => {
        await d.flush().catch(() => undefined)
        await discardDraft(draft.id)
        qc.invalidateQueries({ queryKey: ['write-timeline'] })
        qc.removeQueries({ queryKey: ['write-draft', draft.id] })
        navigate('/write')
        message.open({
          type: 'info',
          content: (
            <span>
              已移入回收区{' '}
              <button
                type="button"
                className="link-btn"
                onClick={() =>
                  void restoreDraft(draft.id).then(() => {
                    qc.invalidateQueries({ queryKey: ['write-timeline'] })
                    navigate(`/write/${draft.id}`)
                  })
                }
              >
                撤销
              </button>
            </span>
          ),
          duration: 8,
        })
      },
    })

  const reload = async () => {
    await qc.invalidateQueries({ queryKey: ['write-draft', draft.id] })
  }

  const words = useMemo(() => countWords(d.body), [d.body])
  const selWords = selection ? countWords(selection.text) : 0
  const locked = archiving || d.status === 'conflict'
  const more = {
    items: [
      { type: 'group' as const, label: '写作提示', children: PROMPTS[draft.kind].map((p) => ({ key: `p:${p}`, label: <span lang="en">{p}</span>, disabled: locked })) },
      { type: 'divider' as const },
      { key: 'discard', label: '移入回收区', danger: true, disabled: archiving },
    ],
    onClick: ({ key }: { key: string }) => (key === 'discard' ? discard() : insertPrompt(key.slice(2))),
  }

  return (
    <div className="ed" data-coach={coachOpen}>
      <div className="ed-main">
        <header className="ed-top">
          <IconButton label="回到写作" onClick={() => navigate('/write')}>
            <ArrowLeft />
          </IconButton>
          <span className="ed-status" role="status" data-status={d.status}>
            {KIND_LABEL[draft.kind]} · {d.status === 'saved' ? <Check width={13} height={13} aria-hidden="true" /> : d.status === 'error' ? <CloudOff width={13} height={13} aria-hidden="true" /> : null}
            {d.status === 'saved' ? `${STATUS.saved} ${time(d.savedAt)}` : STATUS[d.status]}
          </span>
          <span className="grow" />
          <span className="ed-words">{words} 词</span>
          <Dropdown trigger={['click']} menu={more}>
            <IconButton label="更多">
              <MoreHorizontal />
            </IconButton>
          </Dropdown>
          <Pill size="sm" variant={coachOpen ? 'selected' : 'ghost'} icon={<MessageCircle />} aria-pressed={coachOpen} onClick={() => setCoachOpen((v) => !v)}>
            教练
          </Pill>
          <Pill size="sm" variant="primary" onClick={() => (d.body.trim() ? setSheet(true) : void message.warning('先写下一点内容，再归档'))} disabled={locked} aria-busy={archiving}>
            完成
          </Pill>
        </header>

        <div className="ed-scroll">
          <div className="ed-col">
            <p className="ed-where">
              {draft.kind === 'work' ? <NotebookPen width={13} height={13} /> : <Lock width={13} height={13} />}
              {WHERE[draft.kind]}
              {draft.entryId && ' · 正在修改一篇已归档的文章'}
            </p>
            {d.status === 'conflict' && (
              <div className="failed" role="alert">
                <span>这份草稿在别处改过了。你在这里没存上的改动已在本机留了一份。</span>
                <Pill size="sm" onClick={() => void reload()} icon={<RotateCcw />}>
                  载入最新的
                </Pill>
              </div>
            )}
            {d.restored && d.status !== 'conflict' && <p className="sheet-note">已恢复这台设备上没同步的内容。</p>}
            <label className="sr-only" htmlFor="write-title">
              标题
            </label>
            <input id="write-title" className="ed-title" value={d.title} placeholder="Title" onChange={(e) => d.update({ title: e.target.value })} readOnly={locked} lang="en" />
            <label className="sr-only" htmlFor="write-body">
              正文
            </label>
            <textarea
              id="write-body"
              ref={body}
              className="ed-body"
              value={d.body}
              placeholder={PROMPTS[draft.kind][0]}
              onChange={(e) => {
                d.update({ body: e.target.value })
                setSelection(null)
                setProposal(null)
              }}
              onSelect={readSelection}
              onKeyUp={readSelection}
              onMouseUp={readSelection}
              readOnly={locked}
              spellCheck={false}
              lang="en"
            />

            {issues.length > 0 && (
              <section className="ed-lint" aria-label="本地检查">
                <h2>
                  本地检查<span>{issues.length} 处 · 在浏览器里查，不调用模型</span>
                </h2>
                <ul>
                  {issues.slice(0, 12).map((it) => (
                    <li key={`${it.from}-${it.to}-${it.kind}`}>
                      <button
                        type="button"
                        className="wp-issue"
                        onClick={() => {
                          body.current?.focus()
                          body.current?.setSelectionRange(it.from, it.to)
                          readSelection()
                        }}
                      >
                        <mark lang="en">{it.text || '（这里）'}</mark>
                        <span>
                          {issueLabel(it.kind)}
                          <small lang="en">{it.message}</small>
                        </span>
                      </button>
                      {it.replacements.map((r) => (
                        <Pill
                          key={r}
                          size="sm"
                          disabled={locked}
                          onClick={() => {
                            if (d.body.slice(it.from, it.to) !== it.text) return void message.warning('正文已经改过，等检查更新一下')
                            replace(it.from, it.to, r)
                          }}
                        >
                          {r ? `换成 “${r}”` : '删掉'}
                        </Pill>
                      ))}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>

          {proposal ? (
            <Suggestion
              p={proposal}
              onApply={() => {
                apply(proposal)
                setProposal(null)
              }}
              onDrop={() => setProposal(null)}
            />
          ) : (
            selection &&
            !locked && (
              <div className="ed-selbar" role="toolbar" aria-label="选中的这一段">
                <span>已选 {selWords} 词</span>
                <button type="button" onClick={() => void rewrite()} disabled={rewriting} aria-busy={rewriting}>
                  <Sparkles aria-hidden="true" />
                  {rewriting ? '正在改写…' : '改写'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCoachOpen(true)
                    setAskKey((k) => k + 1)
                  }}
                >
                  <MessageCircle aria-hidden="true" />
                  问教练
                </button>
              </div>
            )
          )}
        </div>
      </div>
      {coachOpen && <Coach draft={draft} selection={selection} refs={d.contextRefs} onRefs={(contextRefs) => d.update({ contextRefs })} flush={d.flush} onClose={() => setCoachOpen(false)} focusKey={askKey} disabled={locked} />}
      {sheet && <ArchiveSheet draftId={draft.id} flush={d.flush} archiving={archiving} onArchive={() => void archive()} onGoTo={goTo} onClose={() => setSheet(false)} />}
    </div>
  )
}

/** 改写建议：删掉的划掉、加上的标出来；采用了才改正文。 */
function Suggestion({ p, onApply, onDrop }: { p: EditProposal; onApply: () => void; onDrop: () => void }) {
  return (
    <section className="ed-suggest" aria-label="改写建议">
      <p className="diff" lang="en">
        {diffWords(p.original, p.replacement).map((part, i) => (part.type === 'same' ? <span key={i}>{part.text}</span> : part.type === 'del' ? <del key={i}>{part.text}</del> : <ins key={i}>{part.text}</ins>))}
      </p>
      {(p.summaryZh || p.reasons.length > 0) && <p className="why">{[p.summaryZh, ...p.reasons].filter(Boolean).join('；')}</p>}
      <div className="acts">
        <Pill size="sm" variant="primary" onClick={onApply}>
          采用
        </Pill>
        <Pill size="sm" variant="ghost" onClick={onDrop}>
          不用
        </Pill>
      </div>
    </section>
  )
}

/** 完成：先通读一遍（就是「检查全文」），列出最该改的两三处，点「去改」跳到那一句；看完再归档。检查失败也能直接归档。 */
function ArchiveSheet({ draftId, flush, archiving, onArchive, onGoTo, onClose }: { draftId: string; flush: () => Promise<number>; archiving: boolean; onArchive: () => void; onGoTo: (text: string) => void; onClose: () => void }) {
  const [review, setReview] = useState<Review | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    let live = true
    void (async () => {
      try {
        const r = await reviewDraft(draftId, await flush())
        if (live) setReview(r)
      } catch (e) {
        if (live) setError(e instanceof Error ? e.message : '检查失败了')
      }
    })()
    return () => {
      live = false
    }
  }, [])
  const fixes = (review?.issues ?? []).filter((x) => x.example).slice(0, 3)
  const points = fixes.length ? [] : (review?.priorities ?? []).slice(0, 3)
  const n = fixes.length || points.length
  return (
    <Modal open onCancel={onClose} footer={null} width={540} destroyOnHidden title={n ? `归档前，看一眼这 ${n} 处` : '归档前，看一眼'}>
      <div className="ed-sheet">
        {!review && !error && (
          <p className="sheet-note" role="status">
            正在通读一遍…
          </p>
        )}
        {error && <p className="sheet-note">这次没能检查（{error}），可以直接归档。</p>}
        {fixes.length > 0 && (
          <ol className="fixes" aria-label="最该改的">
            {fixes.map((f, i) => (
              <li key={`${f.category}-${i}`}>
                <span className="sk-cite" aria-hidden="true">
                  {i + 1}
                </span>
                <span className="tx">
                  <span className="en" lang="en">
                    {f.example}
                  </span>
                  <small>
                    {f.category} · {f.explanationZh}
                  </small>
                </span>
                <button type="button" className="wh-link" onClick={() => onGoTo(f.example)}>
                  去改
                </button>
              </li>
            ))}
          </ol>
        )}
        {points.length > 0 && (
          <ol className="fixes" aria-label="最该改的">
            {points.map((x, i) => (
              <li key={x}>
                <span className="sk-cite" aria-hidden="true">
                  {i + 1}
                </span>
                <span className="tx">{x}</span>
              </li>
            ))}
          </ol>
        )}
        {review && !n && <p className="sheet-note">没看到要先改的。</p>}
        {review?.strengths[0] && <p className="sheet-note">做得好：{review.strengths[0]}</p>}
        <div className="acts">
          <Pill variant="primary" onClick={onArchive} disabled={archiving} aria-busy={archiving}>
            归档
          </Pill>
          <button type="button" className="wh-link" onClick={onClose}>
            再改改
          </button>
        </div>
      </div>
    </Modal>
  )
}

/** 停手 1.2 秒以后跑一次本地检查；结果只对应当时的正文，正文变了就等下一次。 */
function useLocalCheck(text: string): LocalIssue[] {
  const [result, setResult] = useState<{ text: string; issues: LocalIssue[] }>({ text: '', issues: [] })
  useEffect(() => {
    let live = true
    const t = setTimeout(() => {
      void checkEnglish(text)
        .then((issues) => live && setResult({ text, issues }))
        .catch(() => undefined)
    }, 1200)
    return () => {
      live = false
      clearTimeout(t)
    }
  }, [text])
  return result.text === text ? result.issues : []
}

// ———— 已归档的文章 ————

/** 刚归档的那几分钟里，写法问题还在后台整理：隔一会儿再看一次。 */
const fresh = (e?: Entry) => !!e && e.learned === undefined && Date.now() - Date.parse(e.updatedAt) < 3 * 60_000

export function WriteEntryPage() {
  const { entryId = '' } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { message } = AntApp.useApp()
  const { open } = useDocPreview()
  const q = useQuery({ queryKey: ['write-entry', entryId], queryFn: () => getEntry(entryId), refetchInterval: (query) => (fresh(query.state.data) ? 1500 : false) })
  const reopen = useMutation({
    mutationFn: () => reopenEntry(entryId),
    onSuccess: (d) => {
      qc.setQueryData(['write-draft', d.id], d)
      qc.invalidateQueries({ queryKey: ['write-timeline'] })
      navigate(`/write/${d.id}`)
    },
    onError: (e) => void message.error(e.message),
  })
  useEffect(() => {
    if (q.data?.learned) qc.invalidateQueries({ queryKey: ['write-learning'] })
  }, [!!q.data?.learned, qc])
  if (q.isPending) return <div className="empty-chat" aria-busy="true" />
  if (q.isError) return <p className="empty-chat">没能打开这篇文章：{q.error.message}</p>
  const e = q.data
  return (
    <div className="ed-read">
      <Pill size="sm" variant="ghost" icon={<ArrowLeft />} onClick={() => navigate('/write')}>
        写作
      </Pill>
      <p className="ed-where">
        {e.kind === 'work' ? (
          e.saved ? (
            <>
              <Check width={13} height={13} />
              已存进知识库「{FOLDER}」
            </>
          ) : (
            '还没存进知识库'
          )
        ) : (
          <>
            <Lock width={13} height={13} />
            私人日记只留在这里，不进知识库
          </>
        )}
        <span>
          · {dayLabel(e.createdAt)} · {countWords(e.body)} 词
        </span>
      </p>
      <article lang="en">
        <h1 className="ed-title">{e.title}</h1>
        <div className="answer ed-text">
          <ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml>
            {e.body}
          </ReactMarkdown>
        </div>
      </article>
      <Learned e={e} />
      <div className="ed-acts">
        <Pill onClick={() => reopen.mutate()} disabled={reopen.isPending}>
          继续编辑
        </Pill>
        {e.saved && (
          <Pill variant="ghost" icon={<BookOpen />} onClick={() => open(e.saved!.knowledgeId, e.saved!.kbId)}>
            在知识库里打开
          </Pill>
        )}
      </div>
    </div>
  )
}

/** 这篇记下的写法：归档以后从这一篇里整理出的问题，和它是第几次出现。 */
function Learned({ e }: { e: Entry }) {
  if (e.learned === undefined)
    return fresh(e) ? (
      <div className="learned" role="status">
        <small>这篇记下的写法</small>
        <span className="tx">正在看这篇的写法…</span>
      </div>
    ) : null
  return (
    <section className="learned" aria-label="这篇记下的写法">
      <small>这篇记下的写法</small>
      {e.learnError ? (
        <span className="tx">{e.learnError}</span>
      ) : e.learned.length ? (
        <ul>
          {e.learned.map((l) => (
            <li key={l.category}>
              <b>{l.category}</b>
              <span className="n">{l.occurrences > 1 ? `第 ${l.occurrences} 次` : '第一次'}</span>
              <p>{l.explanationZh}</p>
              {l.example && <code lang="en">{l.example}</code>}
            </li>
          ))}
        </ul>
      ) : (
        <span className="tx">这篇没记下要注意的写法。</span>
      )}
    </section>
  )
}
